import { cn } from "@/common/helpers/utils";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { InputVariant, inputVariants } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
  searchText?: string;
}

export interface ComboboxPaginationState {
  isLoading: boolean;
  hasMore: boolean;
  remainingCount: number;
  isLoadingMore: boolean;
  onSearchChange: (searchTerm: string) => void;
  onLoadMore: () => void;
}

interface ComboboxProps<Multiple extends boolean = false> {
  options: ComboboxOption[];
  value?: Multiple extends true ? string[] : string;
  onChange: (value: Multiple extends true ? string[] : string) => void;
  onSearchChange?: (value: string) => void;
  searchValue?: string;
  shouldFilter?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  hasMore?: boolean;
  remainingCount?: number;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  loadMoreLabel?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  clearLabel?: string;
  showClearOption?: boolean;
  className?: string;
  disabled?: boolean;
  multiple?: Multiple;
  id?: string;
  "aria-describedby"?: React.AriaAttributes["aria-describedby"];
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
  renderTrigger?: (props: {
    open: boolean;
    value?: Multiple extends true ? string[] : string;
    placeholder?: string;
  }) => React.ReactNode;
  variant?: InputVariant;
}

export function Combobox<Multiple extends boolean = false>({
  options,
  value,
  onChange,
  onSearchChange,
  searchValue,
  shouldFilter = true,
  isLoading = false,
  loadingText,
  hasMore = false,
  remainingCount,
  onLoadMore,
  isLoadingMore = false,
  loadMoreLabel,
  placeholder,
  searchPlaceholder,
  emptyText,
  clearLabel,
  showClearOption = true,
  className,
  disabled = false,
  multiple = false as Multiple,
  id,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  renderTrigger,
  variant,
}: ComboboxProps<Multiple>) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [internalSearchValue, setInternalSearchValue] = useState("");
  const [selectedOptionSnapshot, setSelectedOptionSnapshot] = useState<ComboboxOption | undefined>(undefined);
  const commandListRef = useRef<HTMLDivElement | null>(null);
  const restoreScrollTopRef = useRef<number | null>(null);
  const shouldRestoreScrollRef = useRef(false);

  // Retain previous options during load-more to prevent the list from briefly emptying.
  // Uses the React-approved "adjusting state during render" pattern.
  const [cachedOptions, setCachedOptions] = useState<ComboboxOption[]>([]);
  if (options.length > 0 && options !== cachedOptions) {
    setCachedOptions(options);
  }
  const displayOptions = options.length > 0 ? options : isLoadingMore || isLoading ? cachedOptions : options;

  const selectedValues = Array.isArray(value) ? value : value ? [value as string] : [];

  const selectedOptionFromOptions = !multiple ? displayOptions.find((option) => option.value === value) : undefined;

  if (selectedOptionFromOptions && selectedOptionFromOptions !== selectedOptionSnapshot) {
    setSelectedOptionSnapshot(selectedOptionFromOptions);
  }

  const selectedOption =
    selectedOptionFromOptions ??
    (selectedOptionSnapshot?.value === value && !multiple ? selectedOptionSnapshot : undefined);

  const displaySearchValue = searchValue ?? internalSearchValue;

  const displayPlaceholder = placeholder || t("SelectOption");
  const displaySearchPlaceholder = searchPlaceholder || t("Search");
  const displayEmptyText = emptyText || t("NoResultsFound");
  const displayLoadingText = loadingText || t("Loading");
  const displayClearLabel = clearLabel || t("ClearSelection");
  const displayLoadMoreLabel = loadMoreLabel || t("LoadMore");

  const handleSearchChange = (nextValue: string) => {
    if (searchValue === undefined) {
      setInternalSearchValue(nextValue);
    }

    onSearchChange?.(nextValue);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);

    if (isOpen) {
      return;
    }

    if (searchValue === undefined) {
      setInternalSearchValue("");
    }

    onSearchChange?.("");
  };

  useEffect(() => {
    if (!open || !shouldRestoreScrollRef.current || restoreScrollTopRef.current === null) {
      return;
    }

    if (isLoadingMore) {
      return;
    }

    const currentScrollTop = restoreScrollTopRef.current;

    requestAnimationFrame(() => {
      if (!commandListRef.current) {
        return;
      }

      commandListRef.current.scrollTop = currentScrollTop;
      restoreScrollTopRef.current = null;
      shouldRestoreScrollRef.current = false;
    });
  }, [open, displayOptions.length, hasMore, isLoadingMore]);

  const handleSelect = (optionValue: string) => {
    if (multiple) {
      const currentValues = (Array.isArray(value) ? value : []) as string[];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter((v) => v !== optionValue)
        : [...currentValues, optionValue];
      (onChange as (value: string[]) => void)(newValues);
    } else {
      (onChange as (value: string) => void)(optionValue);
      const selected = displayOptions.find((o) => o.value === optionValue);
      setSelectedOptionSnapshot(selected);
      setOpen(false);
      handleSearchChange("");
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger asChild>
        {renderTrigger ? (
          renderTrigger({ open, value, placeholder: displayPlaceholder })
        ) : (
          <Button
            type="button"
            id={id}
            variant={ButtonVariant.Ghost}
            colorVariant={ButtonColorVariant.Secondary}
            role="combobox"
            aria-describedby={ariaDescribedBy}
            aria-expanded={open}
            aria-invalid={ariaInvalid}
            disabled={disabled}
            className={cn(
              inputVariants({ variant }),
              "h-9 w-full justify-between px-3 font-normal",
              "hover:bg-accent hover:text-accent-foreground",
              className,
            )}
          >
            <span className={cn("flex min-w-0 items-baseline gap-2", !selectedOption && "text-muted-foreground")}>
              {selectedOption ? (
                <>
                  <span className="truncate">{selectedOption.label}</span>
                  {selectedOption.description && (
                    <span className="text-muted-foreground hidden min-w-0 truncate text-xs sm:inline">
                      {selectedOption.description}
                    </span>
                  )}
                </>
              ) : (
                displayPlaceholder
              )}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start" style={{ width: "var(--radix-popover-trigger-width)" }}>
        <Command shouldFilter={shouldFilter}>
          <CommandInput
            placeholder={displaySearchPlaceholder}
            className="h-9"
            value={displaySearchValue}
            onValueChange={handleSearchChange}
          />
          <CommandList ref={commandListRef} onWheel={(e) => e.stopPropagation()}>
            <CommandEmpty>{isLoading ? displayLoadingText : displayEmptyText}</CommandEmpty>
            <CommandGroup>
              {isLoading && displayOptions.length === 0 && (
                <CommandItem value="__loading__" disabled className="text-muted-foreground">
                  <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} className="mr-2" />
                  {displayLoadingText}
                </CommandItem>
              )}
              {showClearOption && selectedOption && !multiple && (
                <CommandItem
                  value={`__clear__${displayClearLabel}`}
                  onSelect={() => {
                    (onChange as (value: string) => void)("");
                    setSelectedOptionSnapshot(undefined);
                    setOpen(false);
                    handleSearchChange("");
                  }}
                  className="text-muted-foreground"
                >
                  {displayClearLabel}
                </CommandItem>
              )}
              {displayOptions.map((option) => (
                <CommandItem
                  key={option.value || "empty"}
                  value={option.value}
                  keywords={[option.label, option.description, option.searchText].filter(
                    (keyword): keyword is string => !!keyword,
                  )}
                  onSelect={() => handleSelect(option.value)}
                  className="min-w-0"
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex min-w-0 flex-1 items-center">
                          <span className="flex min-w-0 flex-1 flex-col">
                            <span className={cn("truncate", option.description && "font-medium")}>{option.label}</span>
                            {option.description && (
                              <span className="text-muted-foreground truncate text-xs">{option.description}</span>
                            )}
                          </span>
                          <Check
                            className={cn(
                              "ml-auto shrink-0",
                              multiple
                                ? selectedValues.includes(option.value)
                                  ? "opacity-100"
                                  : "opacity-0"
                                : value === option.value
                                  ? "opacity-100"
                                  : "opacity-0",
                            )}
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{option.label}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </CommandItem>
              ))}

              {hasMore && (
                <CommandItem
                  value={`__load_more__${remainingCount ?? ""}`}
                  disabled={isLoadingMore}
                  onSelect={() => {
                    if (commandListRef.current) {
                      restoreScrollTopRef.current = commandListRef.current.scrollTop;
                      shouldRestoreScrollRef.current = true;
                    }

                    onLoadMore?.();
                  }}
                  className="justify-between"
                >
                  <span className="flex items-center gap-2">
                    {isLoadingMore && <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />}
                    {displayLoadMoreLabel}
                  </span>
                  {!isLoadingMore && typeof remainingCount === "number" && remainingCount > 0 && (
                    <span className="text-muted-foreground text-xs">{remainingCount} more</span>
                  )}
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
